import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, Redirect } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Eye, EyeOff, CheckCircle, Building, User, Lock, ArrowRight } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getUserFriendlyError } from "@/lib/user-friendly-messages";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

// Schema for email verification flow (requires password)
const emailOnboardingSchema = z.object({
  password: z.string().min(8, { message: "Password must be at least 8 characters" })
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, { message: "Password must contain uppercase, lowercase, and number" }),
  confirmPassword: z.string(),
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  companyName: z.string().min(2, { message: "Company name is required" }),
  companySize: z.string().min(1, { message: "Please select company size" }),
  role: z.string().min(1, { message: "Please select your role" }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Schema for OAuth flow (no password required)
const oauthOnboardingSchema = z.object({
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  companyName: z.string().min(2, { message: "Company name is required" }),
  companySize: z.string().min(1, { message: "Please select company size" }),
  role: z.string().min(1, { message: "Please select your role" }),
});

type EmailOnboardingFormValues = z.infer<typeof emailOnboardingSchema>;
type OAuthOnboardingFormValues = z.infer<typeof oauthOnboardingSchema>;

export default function OnboardingPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showFreePlanDialog, setShowFreePlanDialog] = useState(false);
  
  // More robust URL parameter parsing
  const urlParams = new URLSearchParams(window.location.search);
  const verificationToken = urlParams.get('token');
  const email = urlParams.get('email');
  
  // Debug URL parameters
  console.log('🔍 Onboarding page debug:', { 
    currentURL: window.location.href,
    search: window.location.search,
    verificationToken: verificationToken ? verificationToken.substring(0, 8) + '...' : null,
    email,
    location 
  });

  // Handle two types of onboarding:
  // 1. Email verification flow (has token + email)
  // 2. OAuth flow (authenticated user with onboardingCompleted: false)
  const isEmailVerificationFlow = Boolean(verificationToken && email);
  const isOAuthFlow = Boolean(user && !user.onboardingCompleted && !isEmailVerificationFlow);
  
  console.log('🔍 Flow determination:', { isEmailVerificationFlow, isOAuthFlow, hasUser: !!user });

  // Determine schema and default values based on flow type
  const schema = isOAuthFlow ? oauthOnboardingSchema : emailOnboardingSchema;
  const defaultValues = isOAuthFlow 
    ? {
        firstName: user?.fullName?.split(' ')[0] || "",
        lastName: user?.fullName?.split(' ')[1] || "",
        companyName: "",
        companySize: "",
        role: "",
      }
    : {
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        companyName: "",
        companySize: "",
        role: "",
      };

  const form = useForm<EmailOnboardingFormValues | OAuthOnboardingFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onboardingMutation = useMutation({
    mutationFn: async (data: EmailOnboardingFormValues | OAuthOnboardingFormValues) => {
      if (isOAuthFlow) {
        // For OAuth users, call OAuth onboarding endpoint
        return await apiRequest("/api/auth/complete-oauth-onboarding", {
          method: "POST",
          body: data,
        });
      } else {
        // For email verification users, call regular onboarding endpoint
        return await apiRequest("/api/auth/complete-onboarding", {
          method: "POST",
          body: {
            ...data,
            verificationToken,
            email,
          },
        });
      }
    },
    onSuccess: () => {
      toast({
        title: "Account created successfully!",
        description: "Welcome to SaaSGama!",
      });
      
      // Show dialog first, and DON'T refresh user data automatically
      // The dialog handles will handle the navigation after user choice
      setShowFreePlanDialog(true);
    },
    onError: (error: any) => {
      const errorMessage = getUserFriendlyError(error);
      toast({
        title: "Error",
        description: errorMessage.description || "Failed to create account",
        variant: "destructive",
      });
    },
  });

  const startTrialMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("/api/start-trial", {
        method: "POST",
      });
    },
    onSuccess: () => {
      toast({
        title: "Free trial started",
        description: "Your 14-day trial is active. Welcome to SaaSGama!",
      });

      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-status"] });
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 100);
    },
    onError: (error: any) => {
      const errorMessage = getUserFriendlyError(error);
      toast({
        title: "Could not start trial",
        description: errorMessage.description || "Please choose a paid plan or try again.",
        variant: "destructive",
      });
    },
  });

  // Show a message that the verification link was clicked (but don't verify yet)
  useEffect(() => {
    if (isEmailVerificationFlow && verificationToken && email) {
      console.log('🔐 Email verification flow detected:', { token: verificationToken.substring(0, 8) + '...', email });
      
      toast({
        title: "Email Link Verified!",
        description: "Please complete your registration below to create your account."
      });
    }
  }, [isEmailVerificationFlow, verificationToken, email, toast]);

  const onSubmit = async (data: EmailOnboardingFormValues | OAuthOnboardingFormValues) => {
    onboardingMutation.mutate(data);
  };

  const handleStartTrial = () => {
    startTrialMutation.mutate();
  };

  const handleSelectPlan = () => {
    // Refresh user data before navigating to subscription page (same as free plan)
    queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    setTimeout(() => {
      window.location.href = "/subscription";
    }, 100);
  };

  // Super admin users should skip onboarding and go directly to admin dashboard
  if (user && user.isSuperAdmin) {
    console.log('🔐 Super admin detected, redirecting to admin dashboard');
    return <Redirect to="/admin" />;
  }

  // Only redirect if we're certain this isn't a verification flow
  // Don't redirect if we have verification parameters, even if user isn't loaded yet
  if (!isEmailVerificationFlow && !isOAuthFlow && !verificationToken) {
    console.log('🔄 Redirecting to register - no verification flow detected');
    return <Redirect to="/register" />;
  }
  
  console.log('✅ Staying on onboarding page - verification flow detected');

  return (
    <>
      <title>Complete Your Setup - SaaSGama</title>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/60">
          <div className="max-w-4xl mx-auto px-6 py-3">
            <div className="flex justify-center">
              <SaaSGamaLogo size="lg" />
            </div>
          </div>
        </header>

        {/* Scrollable Content Container */}
        <div className="h-[calc(100vh-70px)] overflow-y-auto">
          {/* Main Content Container */}
          <main className="max-w-4xl mx-auto px-6 py-4 pb-16">
          {/* Modern Progress Indicator */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-emerald-700">Email verified</span>
              </div>
              <span className="text-sm text-slate-500 font-medium">Step 2 of 2</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 h-3 rounded-full w-full shadow-sm"></div>
            </div>
          </div>

          {/* Modern Title Section */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent mb-3">
              Complete Your Setup
            </h1>
            {isOAuthFlow ? (
              <>
                <p className="text-base text-slate-600 mb-3">Tell us about yourself and your company</p>
                <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                  <span className="text-sm text-blue-700">Setting up account for: </span>
                  <strong className="ml-1 text-sm text-blue-800">{user?.email}</strong>
                </div>
              </>
            ) : (
              <>
                <p className="text-base text-slate-600 mb-3">Set up your password and tell us about yourself</p>
                <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                  <span className="text-sm text-blue-700">Creating account for: </span>
                  <strong className="ml-1 text-sm text-blue-800">{email}</strong>
                </div>
              </>
            )}
          </div>

          {/* Modern Form Container */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
            <div className="p-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  {/* Password Section - Only for email verification flow */}
                  {!isOAuthFlow && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                          <Lock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-slate-900">Secure Your Account</h3>
                          <p className="text-sm text-slate-600">Create a strong password</p>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-700">Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Create password"
                                    {...field}
                                    className="pr-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                  >
                                    {showPassword ? (
                                      <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-700">Confirm Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm password"
                                    {...field}
                                    className="pr-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  >
                                    {showConfirmPassword ? (
                                      <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Personal Information */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">Personal Information</h3>
                        <p className="text-sm text-slate-600">Tell us about yourself</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-slate-700">First Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="John" 
                                {...field} 
                                className="h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-slate-700">Last Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Doe" 
                                {...field} 
                                className="h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-emerald-100 to-green-100 rounded-xl flex items-center justify-center">
                        <Building className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">Company Information</h3>
                        <p className="text-sm text-slate-600">About your organization</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-slate-700">Company Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Acme Inc." 
                                {...field} 
                                className="h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="companySize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-700">Company Size</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg">
                                    <SelectValue placeholder="Select size" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="1-10">1-10 employees</SelectItem>
                                  <SelectItem value="11-50">11-50 employees</SelectItem>
                                  <SelectItem value="51-200">51-200 employees</SelectItem>
                                  <SelectItem value="201-500">201-500 employees</SelectItem>
                                  <SelectItem value="500+">500+ employees</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="role"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-slate-700">Your Role</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger className="h-11 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg">
                                    <SelectValue placeholder="Select role" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="ceo">CEO / Founder</SelectItem>
                                  <SelectItem value="cto">CTO / Technical Lead</SelectItem>
                                  <SelectItem value="it-manager">IT Manager</SelectItem>
                                  <SelectItem value="finance-manager">Finance Manager</SelectItem>
                                  <SelectItem value="operations">Operations</SelectItem>
                                  <SelectItem value="procurement">Procurement</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Modern Submit Button */}
                  <div className="pt-8">
                    <Button 
                      type="submit" 
                      disabled={onboardingMutation.isPending}
                      className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      {onboardingMutation.isPending ? (
                        <>
                          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                          Setting up your account...
                        </>
                      ) : (
                        <>
                          Complete Setup
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
          </main>
        </div>
      </div>

      {/* Plan Selection Dialog */}
      <Dialog
        open={showFreePlanDialog}
        onOpenChange={(open) => {
          if (open) {
            setShowFreePlanDialog(true);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md rounded-2xl"
          hideCloseButton
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-gray-900">Choose your plan</DialogTitle>
            <DialogDescription className="text-center text-gray-600">
              Your account has been created successfully. Select a trial or paid plan to activate your workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-3 mt-6">
            <Button 
              onClick={handleStartTrial}
              disabled={startTrialMutation.isPending}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold"
            >
              {startTrialMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting Trial...
                </>
              ) : (
                "Start 14-Day Free Trial"
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSelectPlan}
              disabled={startTrialMutation.isPending}
              className="w-full h-12 rounded-xl border-2 hover:bg-gray-50"
            >
              Choose a Paid Plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
